<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Schemas } from '@shopware/api-client/api-types';
import { ApiClientError } from '@shopware/api-client';

const { getProductRoute } = useProductRoute();
const { getProductCover } = useMedia();
const { pushError, pushSuccess } = useNotifications();
const { t } = useI18n();

const props = defineProps<{
    lineItem: Schemas['LineItem'];
    product: Schemas['Product'];
    miniCart: boolean;
    isInCheckout?: boolean;
}>();

const { lineItem, product } = toRefs(props);
const isLoading = ref(false);

const lineItemCover = getProductCover(lineItem.value.cover, 'xs');

const { getFormattedPrice } = usePrice();
const { refreshCart } = useCart();
const { trackAddToCart, trackRemoveFromCart } = useAnalytics();
const {
    itemOptions,
    removeItem,
    itemTotalPrice,
    itemQuantity,
    isPromotion,
    isRemovable,
    isStackable,
    isDigital,
    changeItemQuantity,
} = useCartItem(lineItem);

const quantity = ref();
syncRefs(itemQuantity, quantity);

const updateQuantity = async (quantityInput: number | undefined) => {
    if (quantityInput === itemQuantity.value) return;

    const addedProductsNumbers = Number(quantityInput) - itemQuantity.value;
    isLoading.value = true;

    try {
        const response = await changeItemQuantity(Number(quantityInput));
        if (addedProductsNumbers > 0) {
            trackAddToCart(product.value, addedProductsNumbers);
        } else {
            trackRemoveFromCart(product.value, Math.abs(addedProductsNumbers));
        }
        // Refresh cart after quantity update
        await refreshCart(response);

        pushSuccess(t('checkout.lineItem.updateQuantity.successMessage', { lineItemName: lineItem.value.label }));
    } catch (error) {
        pushError(t('checkout.lineItem.updateQuantity.errorMessage', { lineItemName: lineItem.value.label }));

        if (error instanceof ApiClientError) {
            console.log(error.details);
        }
    }

    // Make sure that quantity is the same as it is in the response
    quantity.value = itemQuantity.value;

    isLoading.value = false;
};

const removeCartItem = async () => {
    isLoading.value = true;

    try {
        trackRemoveFromCart(product.value, lineItem.value.quantity);
        await removeItem();

        pushSuccess(t('checkout.lineItem.remove.successMessage', { lineItemName: lineItem.value.label }));
    } catch (error) {
        pushError(t('checkout.lineItem.remove.errorMessage', { lineItemName: lineItem.value.label }));

        if (error instanceof ApiClientError) {
            console.log(error.details);
        }
    }

    isLoading.value = false;
};

const updateQuantityOnEnter = $event => {
    if ($event.target !== null) {
        // remove focus from input to trigger update
        $event.target.blur();
    }
};

// allows the user to change the quantity multiple times before firing a single request
const debounceUpdate = useDebounceFn(updateQuantity, 600);
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<div
        class="align-self-stretch md:align-self-center mr-4 min-h-20 w-20 flex-shrink-0 place-content-center overflow-hidden border border-neutral-300 text-sm bg-white"
    >
        <LocaleLink
            v-if="!isPromotion"
            :to="getProductRoute(lineItem)"
            :target="isInCheckout ? '_blank' : '_self'"
        >
            <template v-if="lineItemCover.placeholder">
                <SharedImagePlaceholder :size="'sm'" />
            </template>

            <template v-else>
                <img
                    :src="lineItemCover.url"
                    :alt="lineItemCover.alt"
                    class="h-auto w-full object-cover object-center"
                />
            </template>
        </LocaleLink>
        <div
            v-else-if="isPromotion"
            class="flex h-full w-full items-center justify-center"
        >
            <FormKitIcon
                icon="percent"
                class="block h-16 w-16 text-gray"
            />
        </div>
    </div>

    <div
        class="flex flex-1 flex-col md:flex-row"
        :class="{ 'md:!flex-col': miniCart }"
    >
        <div class="flex flex-1 flex-col">
            <LocaleLink
                :to="getProductRoute(lineItem)"
                :target="isInCheckout ? '_blank' : '_self'"
            >
                <p class="text-sm font-black">
                    {{ lineItem.label }}
                </p>
            </LocaleLink>

            <span v-if="isDigital">{{ $t('checkout.lineItem.digitalProduct') }}</span>

            <p
                v-if="itemOptions"
                class="mt-1 text-sm"
            >
                <span
                    v-for="option in itemOptions"
                    :key="option.group"
                    class="mr-2"
                >
                    {{ option.group }}: {{ option.option }}
                </span>
            </p>
            <span
                v-if="product?.productNumber"
                class="text-sm"
            >
                {{ $t('productIds.itemNumber') }}&nbsp;{{ product.productNumber }}
            </span>
        </div>
        <div
            class="flex flex-col-reverse gap-6 md:flex-row md:place-items-center"
            :class="{ 'md:!flex-col-reverse md:!place-items-start': miniCart }"
        >
            <!-- CUSTOM IDS-168: User can't change quantity in checkout-->
            <div
                v-if="!isInCheckout"
                class="flex flex-row place-items-center gap-2 sm:gap-14"
            >
                <SharedQuantityInput
                    v-if="isStackable"
                    v-model="quantity"
                    :line-item="lineItem"
                    :is-loading="isLoading"
                    @on-update="debounceUpdate"
                    @on-enter="updateQuantityOnEnter($event)"
                />

                <button
                    v-if="isRemovable"
                    type="button"
                    class="bg-transparent ml-auto flex flex-wrap place-items-center gap-2 text-sm text-neutral-800"
                    :class="{ 'text-gray-medium': isLoading }"
                    @click="removeCartItem"
                >
                    <FormKitIcon
                        icon="trash"
                        class="block w-3"
                    />
                    {{ $t('checkout.lineItem.remove.buttonLabel') }}
                </button>
            </div>

            <div
                v-if="itemTotalPrice"
                class="w-24 text-sm font-extrabold md:text-right"
                :class="{ '!text-left': miniCart, 'self-start': isInCheckout }"
            >
                {{ getFormattedPrice(itemTotalPrice) }}
            </div>
        </div>
    </div>
</FormKitLazyProvider>

</template>
